import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from "../lib/utils";
import { IconLayoutNavbarCollapse } from "@tabler/icons-react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export const FloatingDock = ({
    items,
    desktopClassName,
    mobileClassName,
    setShowAccountPopup,
    setShowSchedule,
    setShowMenu
}) => {
    return (
        <>
            <FloatingDockDesktop
                items={items}
                className={desktopClassName}
                setShowAccountPopup={setShowAccountPopup}
                setShowSchedule={setShowSchedule}
                setShowMenu={setShowMenu}
            />
            <FloatingDockMobile
                items={items}
                className={mobileClassName}
                setShowAccountPopup={setShowAccountPopup}
                setShowSchedule={setShowSchedule}
                setShowMenu={setShowMenu}
            />
        </>
    );
};

const FloatingDockMobile = ({
    items,
    className,
    setShowAccountPopup,
    setShowSchedule,
    setShowMenu
}) => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleItemClick = (item) => {
        switch (item.title) {
            case "Account":
                setShowAccountPopup(true);
                break;
            case "Schedule":
                setShowSchedule(true);
                break;
            case "Menu":
                setShowMenu(true);
                break;
            default:
                navigate(item.href);
        }
        setOpen(false);
    };

    return (
        <div className={cn("relative block md:hidden", className)}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        layoutId="nav"
                        className="absolute bottom-full mb-2 inset-x-0 flex flex-col gap-2">
                        {items.map((item, idx) => (
                            <motion.div
                                key={item.title}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                exit={{
                                    opacity: 0,
                                    y: 10,
                                    transition: {
                                        delay: idx * 0.05,
                                    },
                                }}
                                transition={{ delay: (items.length - 1 - idx) * 0.05 }}>
                                <button
                                    onClick={() => handleItemClick(item)}
                                    className={`h-10 w-10 rounded-full ${location.pathname === item.href ? 'bg-[#ff4b1f]' : 'bg-[#ff6b35]'} flex items-center justify-center`}
                                >
                                    <div className="h-4 w-4 text-white">{item.icon}</div>
                                </button>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
            <button
                onClick={() => setOpen(!open)}
                className="h-10 w-10 rounded-full bg-[#ff6b35] flex items-center justify-center">
                <IconLayoutNavbarCollapse className="h-5 w-5 text-white" />
            </button>
        </div>
    );
};

const FloatingDockDesktop = ({
    items,
    className,
    setShowAccountPopup,
    setShowSchedule,
    setShowMenu
}) => {
    let mouseX = useMotionValue(Infinity);
    const navigate = useNavigate();
    const location = useLocation();

    const handleItemClick = (item) => {
        switch (item.title) {
            case "Account":
                setShowAccountPopup(true);
                break;
            case "Schedule":
                setShowSchedule(true);
                break;
            case "Menu":
                setShowMenu(true);
                break;
            default:
                navigate(item.href);
        }
    };

    return (
        <motion.div
            onMouseMove={(e) => mouseX.set(e.pageX)}
            onMouseLeave={() => mouseX.set(Infinity)}
            className={cn(
                "mx-auto hidden md:flex h-16 gap-4 items-end rounded-2xl bg-transparent px-4 pb-3",
                className
            )}>
            {items.map((item) => (
                <IconContainer
                    key={item.title}
                    mouseX={mouseX}
                    {...item}
                    isActive={location.pathname === item.href}
                    onClick={() => handleItemClick(item)}
                />
            ))}
        </motion.div>
    );
};

function IconContainer({
    mouseX,
    title,
    icon,
    href,
    isActive,
    onClick
}) {
    let ref = useRef(null);

    let distance = useTransform(mouseX, (val) => {
        let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
        return val - bounds.x - bounds.width / 2;
    });

    let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
    let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);

    let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
    let heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);

    let width = useSpring(widthTransform, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });
    let height = useSpring(heightTransform, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });

    let widthIcon = useSpring(widthTransformIcon, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });
    let heightIcon = useSpring(heightTransformIcon, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });

    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            ref={ref}
            style={{ width, height }}
            className="relative"
        >
            <motion.div
                className={`absolute inset-0 rounded-full ${isActive ? 'bg-[#ff4b1f]' : 'bg-[#ff6b35]'}`}
            />
            <motion.button
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className="absolute inset-0 rounded-full bg-transparent flex items-center justify-center"
            >
                <AnimatePresence>
                    {hovered && (
                        <motion.div
                            initial={{ opacity: 0, y: 10, x: "-50%" }}
                            animate={{ opacity: 1, y: 0, x: "-50%" }}
                            exit={{ opacity: 0, y: 2, x: "-50%" }}
                            className="px-2 py-0.5 whitespace-pre rounded-md bg-white text-neutral-700 absolute left-1/2 -translate-x-1/2 -top-8 w-fit text-xs"
                        >
                            {title}
                        </motion.div>
                    )}
                </AnimatePresence>
                <motion.div
                    style={{ width: widthIcon, height: heightIcon }}
                    className="flex items-center justify-center text-white"
                >
                    {icon}
                </motion.div>
            </motion.button>
        </motion.div>
    );
}